import { composeClassNames } from "@faedev/editor-utils";
import { ChangeEvent, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import gutter from "src/functions/gutter";
import validateAuthToken from "src/functions/validateAuthToken";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sCouponForm } from "src/styles/sCouponForm.module.scss";
import { sFormControl } from "src/styles/sFormControl.module.scss";
import { sFormLabel } from "src/styles/sFormLabel.module.scss";
import Preloader from "./Preloader";

type Props = {
  className?: (string | string) | null | undefined;
  refetch: () => void;
};

function Coupon({ className, refetch }: Props) {
  const [coupon, setCoupon] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertFailed, setAlertFailed] = useState(false);
  const [alertFailedMessage, setAlertFailedMessage] = useState("");

  useEffect(() => {
    const timeId = setTimeout(() => {
      // After 5 seconds set the show value to false
      setAlertFailed(false);
    }, 5000);

    return () => {
      clearTimeout(timeId);
    };
  }, [alertFailed]);

  const updateCoupon = (e: ChangeEvent<HTMLInputElement>) => {
    setCoupon(e.target.value);
  };

  const checkCoupon = () => {
    setLoading(true);
    fetch(`${constants.Paths.apiUrl}/coupons/apply/${coupon}`, {
      method: "PUT",
      headers: validateAuthToken(getCookieByName(`auth-token`)),
    }).then(async (res) => {
      if (res.ok) {
        // success
        const response = await res.text();
        if (response === "applied") {
          refetch();
          setLoading(false);
          setCoupon("");
        }
      } else {
        // fail
        const response = await res.json();
        setAlertFailed(true);
        setLoading(false);
        if (parseInt(response.statusCode) === 403) {
          setAlertFailedMessage("Invalid coupon.");
        } else {
          switch (response.message) {
            case "invalid client":
              setAlertFailedMessage("Invalid user.");
              break;
            case "invalid coupon":
              setAlertFailedMessage("Invalid coupon.");
              break;
            case "no active order":
            case "no items in order":
            case "zero/negative price":
              setAlertFailedMessage("Order error.");
              break;
            case "requirements - min price":
              setAlertFailedMessage("Minimum order amount not met.");
              break;
            case "coupon already used":
              setAlertFailedMessage("Cannot use multiple coupons.");
              break;
            case "value too low":
              setAlertFailedMessage("Coupon cannot be used.");
              break;
            case "wrong subscription":
              setAlertFailedMessage(
                "The gift coupon can only be applied to a subscription."
              );
              break;
            default:
              setAlertFailedMessage("Unexpected error.");
          }
        }
      }
    });
  };

  return (
    <Form
      id={`CouponFrom`}
      noValidate
      className={composeClassNames([sCouponForm, className, "coupon-form"])}
    >
      <Form.Row className="justify-content-center">
        <Form.Group as={Row} className="mb-3 mt-3">
          <Form.Label column sm={12} md={3} className={sFormLabel}>
            <Translate tKey={`Coupon`} />
          </Form.Label>
          <Col sm={12} md={4} className="coupon-form-control">
            <Form.Control
              className={sFormControl}
              name={`coupon`}
              id={`coupon`}
              onChange={updateCoupon}
              value={coupon}
            />
          </Col>
          <Col sm={12} md={5}>
            <Button
              variant="primary"
              className={composeClassNames([sButton, "btn-submit btn-sm"])}
              onClick={checkCoupon}
              style={gutter(`margin`, `vertical`, `xs`)}
            >
              {loading && <Preloader size="sm" />}
              <Translate tKey={`Use coupon`} />
            </Button>
          </Col>
        </Form.Group>
      </Form.Row>
      {alertFailed && (
        <Alert variant="danger" className={sAlert}>
          <Translate tKey={alertFailedMessage} />
        </Alert>
      )}
    </Form>
  );
}

export default Coupon;
